/**
 * Normalisation for `CrmView.cColumnCondition` — the per-view list of visible
 * columns.
 *
 * WHY THIS EXISTS
 * ---------------
 * `BreadTabulatorSelectColumn` uses `<v-select item-value="field">`, so after a
 * user interaction its v-model holds field-name STRINGS. But its `mounted()`
 * and `showAllColumns()` assigned arrays of column OBJECTS. Whichever shape
 * happened to be live got JSON.stringify'd straight into the database, so
 * production rows hold both shapes — a large share of them the object form,
 * some carrying a leaked `width` from the old in-place column mutation.
 *
 * The consumer then did `selectedColumns.includes(col.field)`, a string compare
 * that never matches an object — hiding every column and rendering a blank
 * table.
 *
 * CONTRACT
 * --------
 *   read  — tolerant of both shapes, forever. Never remove this.
 *   write — only ever emits the canonical string[] shape.
 */

/**
 * @param {string|Array} raw   JSON string or already-parsed array, either shape
 * @param {Array} [columns]    optional column list; when given, unknown keys are dropped
 * @returns {string[]|null}    column keys, or null meaning "no stored preference"
 *
 * Returning null rather than [] matters: an empty array previously meant
 * "hide everything" and produced a blank table for views stored as `[]`.
 * Null lets the caller fall back to the model's default visible set.
 */
export function readColumnCondition(raw, columns) {
  let parsed = raw;

  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  if (!Array.isArray(parsed)) return null;

  const keys = [];
  for (const el of parsed) {
    let key = null;
    if (typeof el === "string") key = el;
    else if (el && typeof el === "object") key = el.field || el.value || null;
    if (key && keys.indexOf(key) === -1) keys.push(key);
  }

  if (!keys.length) return null;

  if (Array.isArray(columns) && columns.length) {
    const known = keys.filter((k) =>
      columns.some((c) => (c.field || c.value || c.key) === k)
    );
    return known.length ? known : null;
  }

  return keys;
}

/**
 * Canonical write path. Accepts either shape, always stores string[].
 * @returns {string} JSON to persist in CrmView.cColumnCondition
 */
export function writeColumnCondition(selected) {
  return JSON.stringify(readColumnCondition(selected) || []);
}

/**
 * Coerce a mixed selection to plain keys without the null semantics — used
 * where "empty means empty" is genuinely correct (e.g. a live v-model).
 */
export function toColumnKeys(selected) {
  return readColumnCondition(selected) || [];
}
